/**
 * useAsyncData Hook
 * 
 * Ersätter 171+ identiska loading state-mönster i kodbasen.
 * Hanterar data-hämtning med loading, error och refetch.
 * 
 * Användning:
 * const { data, loading, error, refetch } = useAsyncData<User[]>('/api/users');
 * const { data } = useAsyncData<Vehicle>('/api/vehicles', { params: { id: 123 } });
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/services/api/client';
import { AxiosRequestConfig } from 'axios';

export interface UseAsyncDataOptions<T> {
  /** Query parameters to send with the request */
  params?: Record<string, any>;
  /** Skip initial fetch (useful for conditional fetching) */
  skip?: boolean;
  /** Transform the response data before storing */
  transform?: (data: any) => T;
  /** Initial data value */
  initialData?: T;
  /** Callback when data is loaded successfully */
  onSuccess?: (data: T) => void;
  /** Callback when an error occurs */
  onError?: (error: Error) => void;
  /** Auto-refresh interval in milliseconds */
  refreshInterval?: number;
  /** Axios request config */
  axiosConfig?: AxiosRequestConfig;
  /** Dependencies that trigger refetch when changed */
  deps?: any[];
}

export interface UseAsyncDataResult<T> {
  /** The fetched data */
  data: T | null;
  /** Loading state */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Manually trigger a refetch */
  refetch: () => Promise<void>;
  /** Reset state to initial values */
  reset: () => void;
  /** Whether initial fetch has completed */
  isInitialLoad: boolean;
  /** Update data manually (for optimistic updates) */
  setData: React.Dispatch<React.SetStateAction<T | null>>;
}

export function useAsyncData<T = any>(
  endpoint: string | null,
  options: UseAsyncDataOptions<T> = {}
): UseAsyncDataResult<T> {
  const {
    params,
    skip = false,
    transform,
    initialData = null,
    onSuccess,
    onError,
    refreshInterval,
    axiosConfig,
    deps = [],
  } = options;

  const [data, setData] = useState<T | null>(initialData);
  const [loading, setLoading] = useState(!skip && !!endpoint);
  const [error, setError] = useState<Error | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    if (!endpoint || skip) {
      setLoading(false);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);

      const response = await api.get(endpoint, {
        params,
        signal: abortControllerRef.current.signal,
        ...axiosConfig,
      });

      if (!isMountedRef.current) return;

      // Handle standard API response format
      let resultData: T;
      if (response.data?.success !== undefined) {
        // Standard { success: true, data: [...] } format
        resultData = response.data.data;
      } else {
        resultData = response.data;
      }

      // Apply transform if provided
      if (transform) {
        resultData = transform(resultData);
      }

      setData(resultData);
      setIsInitialLoad(false);
      onSuccess?.(resultData);
    } catch (err: any) {
      if (!isMountedRef.current) return;
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;

      const error = err instanceof Error ? err : new Error(err.message || 'Unknown error');
      setError(error);
      onError?.(error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [endpoint, skip, JSON.stringify(params), ...deps]);

  const reset = useCallback(() => {
    setData(initialData);
    setError(null);
    setLoading(false);
    setIsInitialLoad(true);
  }, [initialData]);

  // Initial fetch and refetch on deps change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh interval
  useEffect(() => {
    if (!refreshInterval || skip || !endpoint) return;

    const intervalId = setInterval(fetchData, refreshInterval);
    return () => clearInterval(intervalId);
  }, [fetchData, refreshInterval, skip, endpoint]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    reset,
    isInitialLoad,
    setData,
  };
}

/**
 * useAsyncDataList - Convenience wrapper for array data
 */
export function useAsyncDataList<T>(
  endpoint: string | null,
  options: Omit<UseAsyncDataOptions<T[]>, 'initialData'> = {}
): UseAsyncDataResult<T[]> & { isEmpty: boolean; count: number } {
  const result = useAsyncData<T[]>(endpoint, {
    ...options,
    initialData: [],
  });

  return {
    ...result,
    data: result.data || [],
    isEmpty: !result.loading && (result.data?.length ?? 0) === 0,
    count: result.data?.length ?? 0,
  };
}

export default useAsyncData;
